/**
 * Reusable, pure (no-React) render gate shared by dam-ui and the venus pickers.
 *
 * Decides whether an asset should mount `<LottiePlayer>`:
 *  - dotLottie ZIP (`.lottie`) is trusted (the player validates the ZIP; a bad
 *    ZIP falls to the error boundary).
 *  - JSON-based pairs are confirmed with a top-level-key signature scan of the
 *    file's head and, if the head has no keys, its tail — some exporters emit a
 *    large `assets` block (base64 images) first and the metadata keys last. We
 *    never `JSON.parse` and never spawn a Web Worker (venus builds with Rollup,
 *    single-file output, which cannot bundle a worker chunk); only ~64 KB from
 *    each end is read, so files larger than that stay safe.
 */

export type LottieDetectResult =
  | 'dotlottie-zip' // ZIP-backed .lottie — defer to the player, boundary guards a bad ZIP
  | 'lottie-json' // JSON head confirmed as Lottie (top-level signature keys) → render player
  | 'not-lottie' // not a Lottie animation → render the icon, NOT the player
  | 'defer-manual' // large file + CDN ignored Range so no head could be read → "Load animation"

export interface DetectOptions {
  /**
   * @mime - The asset's content type, e.g. `application/json`, `application/zip`,
   *         `application/octet-stream`, `application/zip+dotlottie`.
   */
  mime: string
  /**
   * @extension - Lower-case file extension without the dot (e.g. `lottie`, `json`).
   */
  extension: string
  /**
   * @src - The plain delivery URL. Detection sniffs exactly the bytes the player
   *        will later fetch, so a decision can never disagree with what renders.
   */
  src: string
  /**
   * @signal - Optional AbortSignal so detection is cancelled when a card scrolls
   *           out of view, a modal closes, or a row unmounts.
   */
  signal?: AbortSignal
  /**
   * @headScanBytes - Bytes read from the head for the signature scan. Default 64 KB.
   *                  Never full-parsed.
   */
  headScanBytes?: number
  /**
   * @fileSize - Optional asset size in bytes (informational only; not required
   *             by the head-scan strategy).
   */
  fileSize?: number
}

/**
 * Sync gate 1 ("classification / candidate") — the coarse (MIME, extension) pair
 * match shared by the self-validating venus pickers (`AMV2AssetPickerModal`, legacy
 * `AssetPicker`). Deliberately lets a plain `application/json` + `.json` data file
 * through — gate 2 (`detectLottieRenderable`) is what filters those out, so a plain
 * `.json` never mounts a blank/broken animated player. Pure; no asset-type union.
 */
export const LOTTIE_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['application/json', 'json'],
  ['application/json', 'lottie'],
  ['application/zip', 'lottie'],
  ['application/octet-stream', 'json'],
  ['application/octet-stream', 'lottie'],
  ['application/zip+dotlottie', 'lottie']
]

/**
 * Minimal structural shape the pair match needs. Kept local (not the picker's
 * `AssetData`) so this pure util never depends on a consumer's types. Field casing
 * differs by repo, so both snake_case (`is_dir`) and camelCase (`isDir`) are read.
 */
export interface LottieCandidateAsset {
  content_type?: string | string[]
  file_name?: string
  name?: string
  is_dir?: boolean
  isDir?: boolean
}

/**
 * Gate 1: is this asset even a Lottie candidate? Folders are never candidates.
 * The extension is derived from the filename (the classifier never sees it on the
 * MIME). Returns true for the six accepted pairs — including plain JSON, on purpose.
 */
export const isLottieCandidate = (asset: LottieCandidateAsset | null | undefined): boolean => {
  if (!asset || asset.is_dir || asset.isDir) return false
  const rawMime = Array.isArray(asset.content_type) ? asset.content_type[0] : asset.content_type
  const mime = String(rawMime ?? '').toLowerCase()
  const ext = String(asset.file_name ?? asset.name ?? '').split('.').pop()?.toLowerCase() ?? ''
  return LOTTIE_PAIRS.some(([m, e]) => m === mime && e === ext)
}

const DEFAULT_HEAD_SCAN_BYTES = 64 * 1024

// Canonical Lottie JSON signature (lottie-web / bodymovin). A plain JSON data
// file will practically never carry this exact shape.
export const looksLikeLottieObject = (obj: unknown): boolean => {
  if (!obj || typeof obj !== 'object') return false
  const o = obj as Record<string, unknown>
  return (
    typeof o.v === 'string' && // bodymovin version, e.g. "5.7.4"
    typeof o.fr === 'number' && // frame rate
    typeof o.ip === 'number' && // in-point
    typeof o.op === 'number' && // out-point
    Array.isArray(o.layers) // layer list
  )
}

// Substring scan for the canonical Lottie top-level keys — no JSON.parse, no
// full allocation, no Web Worker. Signature keys usually sit near the start of a
// Lottie object, but an `assets`-first export (large base64 images) can push
// them to the very end — so the caller scans both the head and the tail.
const LOTTIE_KEY_HINTS = ['"fr"', '"ip"', '"op"', '"layers"', '"v"']
const MIN_KEY_HITS = 3

// True when `text` contains at least MIN_KEY_HITS of the signature keys. Scans
// all of the text it is given — no internal size cap; the caller decides how
// many bytes to pass (head or tail slice).
export const hasLottieSignatureKeys = (text: string): boolean =>
  LOTTIE_KEY_HINTS.filter((k) => text.includes(k)).length >= MIN_KEY_HITS

// Head check: the payload must be a JSON object AND carry the signature keys
// within the provided head text.
export const headLooksLikeLottie = (headText: string): boolean =>
  headText.trimStart().startsWith('{') && hasLottieSignatureKeys(headText)

// Reads only the first `byteCount` bytes of `body`, then cancels the rest so a
// large file is never fully downloaded to sniff.
const readPrefixFromStream = async (body: ReadableStream<Uint8Array>, byteCount: number): Promise<string> => {
  const reader = body.getReader()
  const chunks: Uint8Array[] = []
  let received = 0
  try {
    while (received < byteCount) {
      const { done, value } = await reader.read()
      if (done) break
      if (value) {
        chunks.push(value)
        received += value.length
      }
    }
  } finally {
    await reader.cancel().catch(() => undefined)
  }
  const merged = new Uint8Array(received)
  let offset = 0
  for (const chunk of chunks) {
    merged.set(chunk, offset)
    offset += chunk.length
  }
  // Signature keys are ASCII, so a boundary-truncated multibyte char is harmless.
  return new TextDecoder().decode(merged.subarray(0, byteCount))
}

/**
 * Fetches only the head of `src`:
 *  - `206` (server honored `Range`) → returns the head bytes.
 *  - `200` (server ignored `Range`) with a readable stream → reads the first
 *    `byteCount` bytes from the stream and aborts the rest.
 *  - otherwise (error / no stream) → `null`, so the caller can defer.
 */
export const fetchHeadViaRange = async (
  src: string,
  byteCount: number = DEFAULT_HEAD_SCAN_BYTES,
  signal?: AbortSignal
): Promise<string | null> => {
  try {
    const response = await fetch(src, { headers: { Range: `bytes=0-${byteCount - 1}` }, signal })
    if (response.status === 206) {
      return await response.text()
    }
    if (response.status === 200 && response.body) {
      return await readPrefixFromStream(response.body, byteCount)
    }
    return null
  } catch {
    // Network error / abort / no fetch — let the caller decide (defer / fallback).
    return null
  }
}

/**
 * Fetches only the tail of `src` using an HTTP suffix range (`bytes=-N`):
 *  - `206` (server honored the suffix range) → returns the last `byteCount` bytes.
 *  - anything else (`200` full body / `416` / error) → `null`, so we never pull
 *    the whole file just to read its tail.
 */
export const fetchTailViaRange = async (
  src: string,
  byteCount: number = DEFAULT_HEAD_SCAN_BYTES,
  signal?: AbortSignal
): Promise<string | null> => {
  try {
    const response = await fetch(src, { headers: { Range: `bytes=-${byteCount}` }, signal })
    if (response.status === 206) {
      return await response.text()
    }
    return null
  } catch {
    return null
  }
}

const ZIP_MIMES = ['application/zip', 'application/zip+dotlottie', 'application/octet-stream']

/**
 * ZIP `.lottie` is trusted. JSON pairs are confirmed by a top-level-key signature
 * scan of the head and, if the head carries no keys, the tail (an `assets`-first
 * export puts the metadata keys at the very end). Never `JSON.parse`s, never
 * spawns a Web Worker; reads only ~64 KB from each end.
 */
export async function detectLottieRenderable(opts: DetectOptions): Promise<LottieDetectResult> {
  const ext = opts.extension.toLowerCase()
  const mime = opts.mime.toLowerCase()

  if (ext === 'lottie' && ZIP_MIMES.includes(mime)) {
    return 'dotlottie-zip' // ZIP → let the player parse it
  }

  const scanBytes = opts.headScanBytes ?? DEFAULT_HEAD_SCAN_BYTES

  // 1) Head — cheapest; most Lotties carry their signature keys up front.
  const head = await fetchHeadViaRange(opts.src, scanBytes, opts.signal)
  if (head == null) {
    // Couldn't read even the head (CDN ignored Range on a large file).
    return 'defer-manual'
  }
  if (!head.trimStart().startsWith('{')) {
    return 'not-lottie' // not a JSON object at all → never a Lottie
  }
  if (hasLottieSignatureKeys(head)) {
    return 'lottie-json'
  }

  // 2) Head is a JSON object but had no signature keys — an `assets`-first export
  //    (large base64 images) can push the metadata keys to the very end. Scan the
  //    tail before giving up.
  const tail = await fetchTailViaRange(opts.src, scanBytes, opts.signal)
  if (tail != null && hasLottieSignatureKeys(tail)) {
    return 'lottie-json'
  }

  return 'not-lottie'
}
